import React from "react";
import {
  FaTable,
  FaTh,
  FaSearch,
  FaDownload,
  FaList,
  FaListAlt,
  FaMoneyCheckAlt,
} from "react-icons/fa";
import { SecondaryButton } from "../UI/DesignStandards";
import {
  sanitizeErrorMessage,
  logErrorForDebugging,
} from "../../utility/errorSanitization";
import { sanitizeInput } from "../../utils/sanitization";

const segmentActive = "bg-brand-charcoal text-brand-cream";
const segmentIdle = "text-brand-muted hover:text-brand-charcoal";

const PrizeBreakdownControls = ({
  viewMode,
  setViewMode,
  searchTerm,
  setSearchTerm,
  filterEvent,
  setFilterEvent,
  uniqueEvents,
  onExport,
  onExportChecks,
  filteredCount,
  totalCount,
  dataView,
  setDataView,
}) => {
  // Same pattern as team name / scoring search: no sanitization on keystroke (spaces work), sanitize on blur
  const handleSearchChange = (e) => {
    try {
      setSearchTerm(e.target.value.substring(0, 100));
    } catch (err) {
      const sanitizedMessage = sanitizeErrorMessage(err, "update search");
      logErrorForDebugging(err, "update search", sanitizedMessage);
      console.error("Search update failed:", sanitizedMessage);
    }
  };

  const handleSearchBlur = (e) => {
    try {
      const sanitized = sanitizeInput(e.target.value).substring(0, 100);
      if (sanitized !== e.target.value) {
        setSearchTerm(sanitized);
      }
    } catch (err) {
      const sanitizedMessage = sanitizeErrorMessage(err, "update search");
      logErrorForDebugging(err, "update search", sanitizedMessage);
    }
  };

  // Safe filter handler with error handling
  const handleFilterChange = (e) => {
    try {
      setFilterEvent(e.target.value);
    } catch (err) {
      const sanitizedMessage = sanitizeErrorMessage(err, "update filter");
      logErrorForDebugging(err, "update filter", sanitizedMessage);
      console.error("Filter update failed:", sanitizedMessage);
    }
  };

  // Safe export handler with error handling
  const handleExport = () => {
    try {
      onExport();
    } catch (err) {
      const sanitizedMessage = sanitizeErrorMessage(err, "export data");
      logErrorForDebugging(err, "export data", sanitizedMessage);
      console.error("Export failed:", sanitizedMessage);
    }
  };

  // Safe view mode handler with error handling
  const handleViewModeChange = (mode) => {
    try {
      setViewMode(mode);
    } catch (err) {
      const sanitizedMessage = sanitizeErrorMessage(err, "change view mode");
      logErrorForDebugging(err, "change view mode", sanitizedMessage);
      console.error("View mode change failed:", sanitizedMessage);
    }
  };

  // Safe data view handler with error handling
  const handleDataViewChange = (view) => {
    try {
      setDataView(view);
    } catch (err) {
      const sanitizedMessage = sanitizeErrorMessage(err, "change data view");
      logErrorForDebugging(err, "change data view", sanitizedMessage);
      console.error("Data view change failed:", sanitizedMessage);
    }
  };
  return (
    <section role="search" aria-label="Prize filters">
      <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-center justify-between">
        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 flex-1 w-full sm:w-auto">
          <div className="relative w-full sm:w-auto">
            <label htmlFor="bowler-search" className="sr-only">
              Search bowlers
            </label>
            <FaSearch
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-brand-mist"
              aria-hidden="true"
            />
            <input
              id="bowler-search"
              type="text"
              placeholder="Search bowlers..."
              value={searchTerm}
              onChange={handleSearchChange}
              onBlur={handleSearchBlur}
              className="w-full sm:w-64 pl-10 pr-4 py-2 border border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal placeholder-brand-mist focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
              aria-describedby="search-description"
            />
            <div id="search-description" className="sr-only">
              Type to search for bowlers by name
            </div>
          </div>

          <div className="w-full sm:w-auto">
            <label htmlFor="event-filter" className="sr-only">
              Filter by event
            </label>
            <select
              id="event-filter"
              value={filterEvent}
              onChange={handleFilterChange}
              className="w-full sm:w-48 px-4 py-2 border border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
              aria-describedby="filter-description"
            >
              <option value="">All Events</option>
              {uniqueEvents.map((eventName) => (
                <option key={eventName} value={eventName}>
                  {eventName}
                </option>
              ))}
            </select>
            <div id="filter-description" className="sr-only">
              Select an event to filter results
            </div>
          </div>
        </div>

        {/* View Toggle and Export */}
        <div className="flex items-center gap-3 w-full sm:w-auto justify-start sm:justify-end flex-wrap">
          <div
            className="flex bg-brand-cream rounded-lg p-1 border border-brand-charcoal/10 w-full sm:w-auto"
            role="group"
            aria-label="View mode"
          >
            <button
              onClick={() => handleViewModeChange("cards")}
              className={`px-3 py-2 rounded-lg text-sm font-display font-bold transition-colors ${
                viewMode === "cards" ? segmentActive : segmentIdle
              }`}
              aria-pressed={viewMode === "cards"}
              aria-label="View as cards"
            >
              <FaTh className="inline mr-2" aria-hidden="true" />
              <span className="hidden sm:inline">Cards</span>
            </button>
            <button
              onClick={() => handleViewModeChange("table")}
              className={`px-3 py-2 rounded-lg text-sm font-display font-bold transition-colors ${
                viewMode === "table" ? segmentActive : segmentIdle
              }`}
              aria-pressed={viewMode === "table"}
              aria-label="View as table"
            >
              <FaTable className="inline mr-2" aria-hidden="true" />
              <span className="hidden sm:inline">Table</span>
            </button>
          </div>

          <div
            className="flex bg-brand-cream rounded-lg p-1 border border-brand-charcoal/10 w-full sm:w-auto"
            role="group"
            aria-label="Data view"
          >
            <button
              onClick={() => handleDataViewChange("summary")}
              className={`px-3 py-2 rounded-lg text-sm font-display font-bold transition-colors ${
                dataView === "summary" ? segmentActive : segmentIdle
              }`}
              aria-pressed={dataView === "summary"}
              aria-label="View summary by bowler"
            >
              <FaList className="inline mr-2" aria-hidden="true" />
              <span className="hidden sm:inline">Summary</span>
            </button>
            <button
              onClick={() => handleDataViewChange("detail")}
              className={`px-3 py-2 rounded-lg text-sm font-display font-bold transition-colors ${
                dataView === "detail" ? segmentActive : segmentIdle
              }`}
              aria-pressed={dataView === "detail"}
              aria-label="View detailed by event"
            >
              <FaListAlt className="inline mr-2" aria-hidden="true" />
              <span className="hidden sm:inline">Detail</span>
            </button>
          </div>

          <SecondaryButton
            onClick={handleExport}
            icon={FaDownload}
            className="!px-3 !py-2 text-sm"
            aria-label={`Export ${
              dataView === "summary" ? "summary" : "detailed"
            } data as CSV file`}
          >
            <span className="sm:hidden">Export CSV</span>
            <span className="hidden sm:inline">
              Export {dataView === "summary" ? "Summary" : "Detailed"} CSV
            </span>
          </SecondaryButton>
          {onExportChecks ? (
            <SecondaryButton
              onClick={onExportChecks}
              icon={FaMoneyCheckAlt}
              className="!px-3 !py-2 text-sm"
              aria-label="Export Checks"
            >
              Export Checks
            </SecondaryButton>
          ) : null}
        </div>
      </div>

      <div className="mt-4" role="status" aria-live="polite">
        <p className="text-sm text-brand-muted">
          Showing {filteredCount} of {totalCount} bowler
          {totalCount !== 1 ? "s" : ""} with prizes
        </p>
      </div>
    </section>
  );
};

export default PrizeBreakdownControls;
